import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { CommandBarButton, CommandBarSubMenuItem, ListActionButtonProps, TooltipHost } from "sprint-common-web";

import { DeliveryEndpointType } from "../../../../common/types/DeliveryEndpointType";
import { AppConfigContext } from "../../../../core/configuration/Configuration";
import { AuthorizationWrapper } from "../../../../core/security/AuthorizationWrapper";
import { SprintAccessRight } from "../../../../core/security/SprintAccessRight";
import { Environment, environmentResolver } from "../../../../core/types/Environment";
import { AuditListAside, OrderAuditType } from "../../../common/components/audit/AuditListAside";
import { OrderStatus } from "../../../common/types/OrderStatus";
import { useOrdersModuleApi } from "../../../common/useOrdersModuleApi";
import { createOrderFormRoute } from "../../../OrdersRoutes";
import { CompleteRejectedOrderListingDialog } from "../../complete-rejected/CompleteRejectedOrderListingDialog";
import { DeleteOrderDialog } from "../../delete/DeleteOrderButton";
import { canCopyOrderOutputs, CopyOrderOutputsDialog } from "../../copy-outputs/CopyOrderOutputsDialog";
import { RejectOrderListingDialog } from "../../reject/RejectOrderListingDialog";
import {
    ApproveOrderDeliveryListingDialog,
    isOrderApprovable
} from "../approve-delivery/ApproveOrderDeliveryListingDialog";
import { OrderListingContext } from "../context/OrderListingContext";
import { UpdateOrderDeliveryFolderModal } from "../delivery/folder/UpdateOrderDeliveryFolderModal";
import { isDeliveryFolderMissingWhenRequired, OrderListingItem } from "../OrderListingItem";
import { ReprocessOrderDialog } from "../reprocess/ReprocessOrderDialog";
import { SelectVideoDialog } from "../select-video/SelectVideoDialog";
import { RemoveOrderWatermarkDialog } from "../watermark/RemoveOrderWatermarkDialog";
import { RequestOrderWatermarkDialog } from "../watermark/RequestOrderWatermarkDialog";

enum OrderActions {
    Delete,
    CopyOutputs,
    UpdateDeliveryFolder,
    RequestWatermark,
    RemoveWatermark,
    Reprocess,
    AddToMergeList,
    Approve,
    Reject,
    Complete,
    AuditList,
    SelectVideo
}

type OrderActionsListingButtonProps = ListActionButtonProps<OrderListingItem>;

export const OrderActionsListingButton: React.VFC<OrderActionsListingButtonProps> = ({
    item
}: OrderActionsListingButtonProps) => {
    const [state, setState] = useState<OrderActions>();
    const { addItemToMergeList, mergeListItems } = useContext(OrderListingContext);
    const { baseExecutionDetailsUri, environment } = useContext(AppConfigContext);

    const history = useHistory();
    const resolvedEnvironment = environmentResolver(environment);
    const [additionalOptions, setAdditionalOptions] = useState<CommandBarSubMenuItem[]>([]);

    useEffect(() => {
        const options: CommandBarSubMenuItem[] = [
            {
                text: "Delete",
                disabled: !isDeleteEnabled(item),
                onClick: () => setState(OrderActions.Delete)
            },
            {
                text: "Copy outputs",
                disabled: !canCopyOrderOutputs(item.status),
                onClick: () => setState(OrderActions.CopyOutputs)
            },
            {
                text: "Update delivery folder",
                disabled: isUpdateDeliveryFolderDisabled(item),
                onClick: () => setState(OrderActions.UpdateDeliveryFolder)
            },
            {
                text: "Reprocess",
                disabled: isReprocessDisabled(item),
                onClick: () => setState(OrderActions.Reprocess)
            },
            {
                text: "Add to order merge list",
                disabled: mergeListItems.length === 2,
                onClick: () => addItemToMergeList(item)
            },
            {
                text: "Create similar",
                onClick: () => history.push(`${createOrderFormRoute.path}?sourceOrderId=${item.id}`),
                testId: "actions-additional-create-similar"
            },
            {
                text: "Audit",
                onClick: () => setState(OrderActions.AuditList)
            }
        ];
        if (resolvedEnvironment !== Environment.Prod) {
            options.push(
                {
                    text: "Request watermark",
                    disabled: isRequestWatermarkDisabled(item),
                    onClick: () => setState(OrderActions.RequestWatermark)
                },
                {
                    text: "Remove watermark",
                    disabled: isRemoveWatermarkDisabled(item),
                    onClick: () => setState(OrderActions.RemoveWatermark)
                }
            );
        }
        setAdditionalOptions(options);
    }, [resolvedEnvironment, mergeListItems.length, item]);

    return (
        <AuthorizationWrapper accessRights={[SprintAccessRight.OrdAdmin]}>
            <TooltipHost content="Additional">
                <CommandBarButton
                    icon="MoreVertical"
                    menuItems={[
                        {
                            text: "Status",
                            subMenuProps: {
                                items: [
                                    {
                                        text: "Approve delivery",
                                        disabled: !isOrderApprovable(item),
                                        onClick: () => setState(OrderActions.Approve)
                                    },
                                    {
                                        text: "Reject",
                                        disabled: !isRejectEnabled(item.status) || !!item.buzzId,
                                        onClick: () => setState(OrderActions.Reject)
                                    },
                                    {
                                        text: "Complete",
                                        disabled: !canOrderBeCompleted(item),
                                        onClick: () => setState(OrderActions.Complete)
                                    }
                                ]
                            }
                        },
                        {
                            text: "Workflow",
                            subMenuProps: {
                                items: [
                                    {
                                        text: "Processing",
                                        disabled: !item.processingExecutionArn,
                                        onClick: () =>
                                            window.open(`${baseExecutionDetailsUri}/${item.processingExecutionArn}`)
                                    },
                                    {
                                        text: "Delivery",
                                        disabled: !item.deliveryExecutionArn,
                                        onClick: () =>
                                            window.open(`${baseExecutionDetailsUri}/${item.deliveryExecutionArn}`)
                                    },
                                    {
                                        text: "Select video",
                                        onClick: () => setState(OrderActions.SelectVideo)
                                    }
                                ]
                            }
                        },
                        {
                            text: "Additional",
                            subMenuProps: {
                                items: additionalOptions
                            }
                        }
                    ]}
                    testId="order-actions-button"
                />
            </TooltipHost>
            <StatusDialogManager status={state} item={item} onClose={() => setState(undefined)} />
        </AuthorizationWrapper>
    );
};

const StatusDialogManager = ({
    status,
    item,
    onClose
}: {
    status?: OrderActions;
    item: OrderListingItem;
    onClose: () => void;
}) => {
    const { getCloudOrderAuditList } = useOrdersModuleApi();

    switch (status) {
        case OrderActions.Delete:
            return <DeleteOrderDialog isOpen item={item} onClose={onClose} />;

        case OrderActions.CopyOutputs:
            return <CopyOrderOutputsDialog isOpen orderId={item.id} onClose={onClose} />;

        case OrderActions.UpdateDeliveryFolder:
            return <UpdateOrderDeliveryFolderModal isOpen item={item} onCancel={onClose} onSubmit={onClose} />;

        case OrderActions.RequestWatermark:
            return <RequestOrderWatermarkDialog isOpen item={item} onCancel={onClose} onSubmit={onClose} />;

        case OrderActions.RemoveWatermark:
            return <RemoveOrderWatermarkDialog isOpen item={item} onCancel={onClose} onSubmit={onClose} />;

        case OrderActions.Reprocess:
            return <ReprocessOrderDialog isOpen item={item} onCancel={onClose} onSubmit={onClose} />;

        case OrderActions.Approve:
            return <ApproveOrderDeliveryListingDialog isOpen item={item} onClose={onClose} />;

        case OrderActions.Reject:
            return <RejectOrderListingDialog isOpen item={item} onClose={onClose} />;

        case OrderActions.Complete:
            return <CompleteRejectedOrderListingDialog isOpen item={item} onClose={onClose} />;

        case OrderActions.AuditList:
            return (
                <AuditListAside
                    isOpen
                    itemId={item.id}
                    onClose={onClose}
                    getOrderAuditList={getCloudOrderAuditList}
                    type={OrderAuditType.Cloud}
                />
            );
        case OrderActions.SelectVideo:
            return <SelectVideoDialog isOpen item={item} onClose={onClose} />;
        default:
            return <></>;
    }
};

const isDeleteEnabled = (order: OrderListingItem) =>
    (order.status === OrderStatus.Pending ||
        order.status === OrderStatus.Rejected ||
        order.status === OrderStatus.DeliveryCompleted) &&
    !order.buzzId;

const isUpdateDeliveryFolderDisabled = (order: OrderListingItem) =>
    !!order.deliveryFolder || order.deliveryEndpointType !== DeliveryEndpointType.Aspera;

const isRequestWatermarkDisabled = (order: OrderListingItem) =>
    order.isWatermark || order.status === OrderStatus.Approved;

const isRemoveWatermarkDisabled = (order: OrderListingItem) =>
    !order.isWatermark || order.status === OrderStatus.Approved;

const isRejectEnabled = (status: OrderStatus) =>
    status === OrderStatus.Pending ||
    status === OrderStatus.EncodingFailed ||
    status === OrderStatus.Encoded ||
    status === OrderStatus.DeliveryFailed;

const isReprocessDisabled = (item: OrderListingItem) =>
    item.status !== OrderStatus.Encoded &&
    item.status !== OrderStatus.EncodingFailed &&
    item.status !== OrderStatus.DeliveryFailed &&
    item.status !== OrderStatus.DeliveryError;

const canOrderBeCompleted = (item: OrderListingItem) =>
    item.status !== OrderStatus.DeliveryCompleted &&
    item.status !== OrderStatus.Cancelled &&
    item.status !== OrderStatus.Overridden &&
    !isDeliveryFolderMissingWhenRequired(item);
